import React from "react";
import { AccountIcon, Co2Icon, DataIcon, HomeIcon, ServicesIcon, SettingsIcon, WhatIfIcon } from "../assets/icons";
import { AppMenuKey, AppRoutes } from "../common/routesUtils";
import { FilterContexType } from "../components/layout/ViewHeader";
import { PageType } from "./page.types";

// Breadcrumb types
export type StaticBreadcrumb = {
	icon?: React.ElementType;
	label: AppMenuKey;
	path?: AppRoutes;
	isLabelOnly?: boolean; // used just to show labels without link
};

export type StaticPageLayout = {
	filterContext?: FilterContexType;
	breadcrumbs?: StaticBreadcrumb[];
	title?: string;
	subtitle?: string;
};

// Helper: create one or multiple breadcrumbs
const createBreadcrumbs = (
	...crumbs: Array<{ icon?: React.ElementType; label: AppMenuKey; path?: AppRoutes; isLabelOnly?: boolean }>
): StaticBreadcrumb[] => crumbs;

// Page layout configuration
export const PAGE_LAYOUT: Record<PageType, StaticPageLayout> = {
	// Home
	[PageType.Home]: {
		breadcrumbs: createBreadcrumbs({
			icon: HomeIcon,
			label: AppMenuKey.PostSignInHome,
			path: AppRoutes.PostSignInHome,
		}),
	},

	// Data Collection
	[PageType.DataCollectionMonitor]: {
		breadcrumbs: createBreadcrumbs(
			{ icon: DataIcon, label: AppMenuKey.DataCollection, isLabelOnly: true },
			{ label: AppMenuKey.DataCollectionMonitor, path: AppRoutes.DataCollectionMonitor }
		),
	},
	[PageType.DataCollectionImport]: {
		breadcrumbs: createBreadcrumbs(
			{ icon: DataIcon, label: AppMenuKey.DataCollection, isLabelOnly: true },
			{ label: AppMenuKey.DataCollectionImport, path: AppRoutes.DataCollectionImport }
		),
	},
	[PageType.DataCollectionConfiguration]: {
		breadcrumbs: createBreadcrumbs(
			{ icon: DataIcon, label: AppMenuKey.DataCollection, isLabelOnly: true },
			{ label: AppMenuKey.DataCollectionConfiguration, path: AppRoutes.DataCollectionConfiguration }
		),
		title: PageType.DataCollectionConfiguration,
	},
	[PageType.DataCollectionConfigurationOperationalUnits]: {
		filterContext: ["nodes"],
		breadcrumbs: createBreadcrumbs(
			{ icon: DataIcon, label: AppMenuKey.DataCollection, isLabelOnly: true },
			{
				label: AppMenuKey.DataCollectionConfigurationOperationalUnits,
				path: AppRoutes.DataCollectionConfigurationOperationalUnits,
			}
		),
		title: PageType.DataCollectionConfigurationOperationalUnits,
	},

	[PageType.DataCollectionConfigurationOperationalUnitsAddUnit]: {
		breadcrumbs: createBreadcrumbs(
			{ icon: DataIcon, label: AppMenuKey.DataCollection, isLabelOnly: true },
			{
				label: AppMenuKey.DataCollectionConfigurationOperationalUnits,
				path: AppRoutes.DataCollectionConfigurationOperationalUnits,
			},
			{
				label: AppMenuKey.DataCollectionConfigurationOperationalUnitsAddUnit,
				path: AppRoutes.DataCollectionConfigurationOperationalUnitsAddUnit,
			}
		),
		title: PageType.DataCollectionConfigurationOperationalUnitsAddUnit,
	},

	[PageType.DataCollectionConfigurationOperationalUnitsDetail]: {
		breadcrumbs: createBreadcrumbs(
			{ icon: DataIcon, label: AppMenuKey.DataCollection, isLabelOnly: true },
			{
				label: AppMenuKey.DataCollectionConfigurationOperationalUnits,
				path: AppRoutes.DataCollectionConfigurationOperationalUnits,
			},
			{
				label: AppMenuKey.DataCollectionConfigurationOperationalUnitsDetail,
				path: AppRoutes.DataCollectionConfigurationOperationalUnitsDetail,
			}
		),
		title: PageType.DataCollectionConfigurationOperationalUnitsDetail,
	},

	// Results
	[PageType.ResultsOverview]: {
		filterContext: ["trips", "nodes"],
		breadcrumbs: createBreadcrumbs(
			{ icon: Co2Icon, label: AppMenuKey.Results, isLabelOnly: true },
			{ label: AppMenuKey.ResultsOverview, path: AppRoutes.ResultsOverview }
		),
	},
	[PageType.ResultsDetails]: {
		breadcrumbs: createBreadcrumbs(
			{ icon: Co2Icon, label: AppMenuKey.Results, isLabelOnly: true },
			{ label: AppMenuKey.ResultsDetails, path: AppRoutes.ResultsDetails }
		),
	},
	[PageType.ResultsReport]: {
		breadcrumbs: createBreadcrumbs(
			{ icon: Co2Icon, label: AppMenuKey.Results, isLabelOnly: true },
			{ label: AppMenuKey.ResultsReport, path: AppRoutes.ResultsReport }
		),
	},

	// Dashboard & Insights
	[PageType.ResultsDashboardInsights]: {
		filterContext: ["trips"],
		breadcrumbs: createBreadcrumbs(
			{ icon: Co2Icon, label: AppMenuKey.Results, isLabelOnly: true },
			{ label: AppMenuKey.ResultsDashboardInsights, path: AppRoutes.ResultsDashboardInsights }
		),
	},
	[PageType.ResultsDashboardInsightsDetail]: {
		filterContext: ["trips"],
		breadcrumbs: createBreadcrumbs(
			{ icon: Co2Icon, label: AppMenuKey.Results, isLabelOnly: true },
			{ label: AppMenuKey.ResultsDashboardInsights, path: AppRoutes.ResultsDashboardInsights },
			{ label: AppMenuKey.ResultsDashboardInsightsDetail, path: AppRoutes.ResultsDashboardInsightsDetail }
		),
	},

	// Transport
	[PageType.ResultsTransport]: {
		filterContext: ["trips", "nodes"],
		breadcrumbs: createBreadcrumbs(
			{ icon: Co2Icon, label: AppMenuKey.Results, isLabelOnly: true },
			{ label: AppMenuKey.ResultsTransport, path: AppRoutes.ResultsTransport }
		),
	},
	[PageType.ResultsTransportGreenRouter]: {
		filterContext: ["trips", "nodes"],
		breadcrumbs: createBreadcrumbs(
			{ icon: Co2Icon, label: AppMenuKey.Results, isLabelOnly: true },
			{ label: AppMenuKey.ResultsTransport, path: AppRoutes.ResultsTransport },
			{ label: AppMenuKey.ResultsTransportGreenrouter, path: AppRoutes.ResultsTransportGreenrouter }
		),
	},
	[PageType.ResultsTransportGreenRouterEntities]: {
		filterContext: ["trips", "nodes"],
		breadcrumbs: createBreadcrumbs(
			{ icon: Co2Icon, label: AppMenuKey.Results, isLabelOnly: true },
			{ label: AppMenuKey.ResultsTransport, path: AppRoutes.ResultsTransport },
			{ label: AppMenuKey.ResultsTransportGreenrouter, path: AppRoutes.ResultsTransportGreenrouter }
		),
	},
	[PageType.ResultsTransportGreenRouterDetail]: {
		filterContext: ["trips", "nodes"],
		breadcrumbs: createBreadcrumbs(
			{ icon: Co2Icon, label: AppMenuKey.Results, isLabelOnly: true },
			{ label: AppMenuKey.ResultsTransport, path: AppRoutes.ResultsTransport },
			{ label: AppMenuKey.ResultsTransportGreenrouter, path: AppRoutes.ResultsTransportGreenrouter },
			{ label: AppMenuKey.ResultsTransportGreenrouterDetail, path: AppRoutes.ResultsTransportGreenrouterDetail }
		),
	},
	[PageType.ResultsTransportGreenRouterEntityDetail]: {
		filterContext: ["trips", "nodes"],
		breadcrumbs: createBreadcrumbs(
			{ icon: Co2Icon, label: AppMenuKey.Results, isLabelOnly: true },
			{ label: AppMenuKey.ResultsTransport, path: AppRoutes.ResultsTransport },
			{ label: AppMenuKey.ResultsTransportGreenrouter, path: AppRoutes.ResultsTransportGreenrouter },
			{
				label: AppMenuKey.ResultsTransportGreenrouterDetail,
				path: AppRoutes.ResultsTransportGreenrouterEntityDetail,
			}
		),
	},

	[PageType.ResultsTransportThirdParty]: {
		filterContext: ["trips", "nodes"],
		breadcrumbs: createBreadcrumbs(
			{ icon: Co2Icon, label: AppMenuKey.Results, isLabelOnly: true },
			{ label: AppMenuKey.ResultsTransport, path: AppRoutes.ResultsTransport },
			{ label: AppMenuKey.ResultsTransportThirdParty, path: AppRoutes.ResultsTransportThirdParty }
		),
	},
	[PageType.ResultsTransportThirdPartyDetail]: {
		filterContext: ["trips", "nodes"],
		breadcrumbs: createBreadcrumbs(
			{ icon: Co2Icon, label: AppMenuKey.Results, isLabelOnly: true },
			{ label: AppMenuKey.ResultsTransport, path: AppRoutes.ResultsTransport },
			{ label: AppMenuKey.ResultsTransportThirdParty, path: AppRoutes.ResultsTransportThirdParty },
			{ label: AppMenuKey.ResultsTransportThirdPartyDetail, path: AppRoutes.ResultsTransportThirdPartyDetail }
		),
	},

	// Operational Units
	[PageType.ResultsOperationalUnits]: {
		filterContext: ["nodes"],
		breadcrumbs: createBreadcrumbs(
			{ icon: Co2Icon, label: AppMenuKey.Results, isLabelOnly: true },
			{ label: AppMenuKey.ResultsOperationalUnits, path: AppRoutes.ResultsOperationalUnits }
		),
	},
	[PageType.ResultsOperationalUnitsGreenRouter]: {
		filterContext: ["nodes"],
		breadcrumbs: createBreadcrumbs(
			{ icon: Co2Icon, label: AppMenuKey.Results, isLabelOnly: true },
			{ label: AppMenuKey.ResultsOperationalUnits, path: AppRoutes.ResultsOperationalUnits },
			{ label: AppMenuKey.ResultsOperationalUnitsGreenRouter, path: AppRoutes.ResultsOperationalUnitsGreenRouter }
		),
	},
	[PageType.ResultsOperationalUnitsGreenRouterDetail]: {
		filterContext: ["nodes"],
		breadcrumbs: createBreadcrumbs(
			{ icon: Co2Icon, label: AppMenuKey.Results, isLabelOnly: true },
			{ label: AppMenuKey.ResultsOperationalUnits, path: AppRoutes.ResultsOperationalUnits },
			{
				label: AppMenuKey.ResultsOperationalUnitsGreenRouter,
				path: AppRoutes.ResultsOperationalUnitsGreenRouter,
			},
			{
				label: AppMenuKey.ResultOperationalUnitsGreerouterNode,
				path: AppRoutes.ResultOperationalUnitsGreerouterNode,
			}
		),
	},
	[PageType.ResultsOperationalUnitsThirdParty]: {
		filterContext: ["nodes"],
		breadcrumbs: createBreadcrumbs(
			{ icon: Co2Icon, label: AppMenuKey.Results, isLabelOnly: true },
			{ label: AppMenuKey.ResultsOperationalUnits, path: AppRoutes.ResultsOperationalUnits },
			{ label: AppMenuKey.ResultsOperationalUnitsThirdParty, path: AppRoutes.ResultsOperationalUnitsThirdParty }
		),
	},
	[PageType.ResultsOperationalUnitsThirdPartyDetail]: {
		filterContext: ["nodes"],
		breadcrumbs: createBreadcrumbs(
			{ icon: Co2Icon, label: AppMenuKey.Results, isLabelOnly: true },
			{ label: AppMenuKey.ResultsOperationalUnits, path: AppRoutes.ResultsOperationalUnits },
			{ label: AppMenuKey.ResultsOperationalUnitsThirdParty, path: AppRoutes.ResultsOperationalUnitsThirdParty },
			{
				label: AppMenuKey.ResultsOperationalUnitsThirdPartyDetail,
				path: AppRoutes.ResultsOperationalUnitsThirdPartyDetail,
			}
		),
	},

	// What If
	[PageType.WhatIfCarbonBudget]: {
		breadcrumbs: createBreadcrumbs(
			{ icon: WhatIfIcon, label: AppMenuKey.WhatIf, isLabelOnly: true },
			{ label: AppMenuKey.WhatIfCarbonBudget, path: AppRoutes.WhatIfCarbonBudget }
		),
	},
	[PageType.WhatIfSimulation]: {
		breadcrumbs: createBreadcrumbs(
			{ icon: WhatIfIcon, label: AppMenuKey.WhatIf, isLabelOnly: true },
			{ label: AppMenuKey.WhatIfSimulation, path: AppRoutes.WhatIfSimulation }
		),
	},
	[PageType.WhatIfRootCause]: {
		breadcrumbs: createBreadcrumbs(
			{ icon: WhatIfIcon, label: AppMenuKey.WhatIf, isLabelOnly: true },
			{ label: AppMenuKey.WhatIfRootCause, path: AppRoutes.WhatIfRootCause }
		),
		title: PageType.WhatIfRootCause,
		subtitle: PageType.WhatIfRootCause,
	},
	[PageType.WhatIfRootCauseResults]: {
		breadcrumbs: createBreadcrumbs(
			{ icon: WhatIfIcon, label: AppMenuKey.WhatIf, isLabelOnly: true },
			{ label: AppMenuKey.WhatIfRootCause, path: AppRoutes.WhatIfRootCause },
			{ label: AppMenuKey.WhatIfRootCauseResults, path: AppRoutes.WhatIfRootCauseResults }
		),
		title: PageType.WhatIfRootCauseResults,
		subtitle: PageType.WhatIfRootCauseResults,
	},

	// Additional Services
	[PageType.AdditionalServicesCarbonSnap]: {
		breadcrumbs: createBreadcrumbs(
			{ icon: ServicesIcon, label: AppMenuKey.AdditionalServices, isLabelOnly: true },
			{ label: AppMenuKey.AdditionalServicesCarbonSnap, path: AppRoutes.AdditionalServicesCarbonSnap }
		),
		title: PageType.AdditionalServicesCarbonSnap,
	},
	[PageType.AdditionalServicesCarbonSnapResults]: {
		breadcrumbs: createBreadcrumbs(
			{ icon: ServicesIcon, label: AppMenuKey.AdditionalServices, isLabelOnly: true },
			{ label: AppMenuKey.AdditionalServicesCarbonSnap, path: AppRoutes.AdditionalServicesCarbonSnap },
			{
				label: AppMenuKey.AdditionalServicesCarbonSnapResults,
				path: AppRoutes.AdditionalServicesCarbonSnapResults,
			}
		),
	},
	[PageType.AdditionalServicesDocumentation]: {
		breadcrumbs: createBreadcrumbs(
			{ icon: ServicesIcon, label: AppMenuKey.AdditionalServices, isLabelOnly: true },
			{ label: AppMenuKey.AdditionalServicesDocumentation, path: AppRoutes.AdditionalServicesDocumentation }
		),
	},
	[PageType.AdditionalServicesTourWizard]: {
		breadcrumbs: createBreadcrumbs(
			{ icon: ServicesIcon, label: AppMenuKey.AdditionalServices, isLabelOnly: true },
			{ label: AppMenuKey.AdditionalServicesTourWizard, path: AppRoutes.AdditionalServicesTourWizard }
		),
	},

	// Settings
	[PageType.SettingsAdmin]: {
		breadcrumbs: createBreadcrumbs(
			{ icon: SettingsIcon, label: AppMenuKey.Settings, isLabelOnly: true },
			{ label: AppMenuKey.SettingsAdmin, path: AppRoutes.SettingsAdmin }
		),
	},
	[PageType.SettingsAuthApi]: {
		breadcrumbs: createBreadcrumbs(
			{ icon: SettingsIcon, label: AppMenuKey.Settings, isLabelOnly: true },
			{ label: AppMenuKey.SettingsAuthApi, path: AppRoutes.SettingsAuthApi }
		),
	},
	[PageType.SettingsPersonalised]: {
		breadcrumbs: createBreadcrumbs(
			{ icon: SettingsIcon, label: AppMenuKey.Settings, isLabelOnly: true },
			{ label: AppMenuKey.SettingsPersonalised, path: AppRoutes.SettingsPersonalised }
		),
	},

	// Account
	[PageType.Account]: {
		breadcrumbs: createBreadcrumbs(
			{ icon: AccountIcon, label: AppMenuKey.Account, isLabelOnly: true },
			{ label: AppMenuKey.Account, path: AppRoutes.Account }
		),
	},
};
